import cx from "classnames";
import React, { useMemo } from "react";
import type { Sort } from "./Sort";
import { easingSoft, easingSoftMs, fonts, zIndex } from "..";
import Ellipsis from "../Ellipsis";
import { color } from "../index";
import {
  SCROLL_LEFT_CLASSNAME,
  SCROLL_RIGHT_CLASSNAME,
  STICKY_BORDER_LEFT_CLASSNAME,
  STICKY_BORDER_RIGHT_CLASSNAME,
  VERTICAL_SEPARATOR_CLASSNAME,
} from "./Td";
import {
  RowSelector,
  ROW_SELECTOR_PADDING_PX,
  ROW_SELECTOR_SIZE_PX,
  SELECTABLE_ROWS_CLASSNAME,
} from "./useSelectedRows";
import t from "../i18n";
import type { JustificationProps } from "./ColumnMetaData";
import { colors, spacings } from "../Theme";
import { css } from "@linaria/core";
import { ArrowLeftSFill } from "../IconV2/react";

type SortOrder = "asc" | "desc";
interface P<Column extends string> extends JustificationProps {
  sortable?: boolean;
  defaultSortDirection?: SortOrder;
  onSort?: (sort: Sort<Column>) => void;
  sort?: Sort<Column>;
  column: Column;
  diagonal?: boolean;
  className?: string;
  verticalSeparator?: string | boolean;
  label: React.ReactNode;
  onSelectAllRows: ((value: boolean) => void) | null;
  selectAllStatus: "empty" | "full" | "partial";
}

const Th = <Column extends string>({
  defaultSortDirection = "asc",
  className,
  stickyBorder = true,
  diagonal,
  alignment = "left",
  label,
  sortable = false,
  onSort,
  sort,
  column,
  verticalSeparator,
  sticky,
  stickyDirection,
  onSelectAllRows,
  selectAllStatus,
  minWidth,
  maxWidth,
  width,
}: P<Column>) => {
  const handleSortClick = React.useCallback(() => {
    if (sortable && onSort)
      onSort({
        column,
        direction: nextDirection(
          sort?.column === column ? sort.direction : null,
          defaultSortDirection,
        ),
      });
  }, [column, defaultSortDirection, onSort, sort, sortable]);

  const handleSelectChange = React.useCallback(() => {
    if (!onSelectAllRows) throw new Error("Missing onSelectAllRows");
    onSelectAllRows(selectAllStatus === "empty");
  }, [onSelectAllRows, selectAllStatus]);

  const sorted = useMemo(
    () => sort?.direction && sort.column === column,
    [column, sort?.column, sort?.direction],
  );

  return (
    <th
      data-vertical-separator-label={
        verticalSeparator && typeof verticalSeparator === "string" ? verticalSeparator : undefined
      }
      className={cx(tableHead, className, {
        diagonal,
        sortable,
        [VERTICAL_SEPARATOR_CLASSNAME]: verticalSeparator,
        sticky: typeof sticky === "number" || sticky === true,
        [stickyDirection && stickyDirection === "right"
          ? STICKY_BORDER_RIGHT_CLASSNAME
          : STICKY_BORDER_LEFT_CLASSNAME]: stickyBorder,
        sorted,
        left: diagonal || alignment === "left",
        right: !diagonal && alignment === "right",
        center: !diagonal && alignment === "center",
      })}
      style={React.useMemo(
        () => ({
          [stickyDirection === "right" ? "right" : "left"]:
            typeof sticky === "number" || sticky === true
              ? `${typeof sticky === "number" ? sticky : 0}px`
              : "initial",
          minWidth: minWidth || width || 0,
          maxWidth: maxWidth || width || "initial",
        }),
        [maxWidth, minWidth, sticky, stickyDirection, width],
      )}
      onClick={handleSortClick}
    >
      <>
        {onSelectAllRows ? (
          <RowSelector
            smallIndicator
            onChange={handleSelectChange}
            checked={selectAllStatus === "full"}
            partial={selectAllStatus === "partial"}
            overlay={
              selectAllStatus === "partial" || selectAllStatus === "full"
                ? t("table.unselect_all")
                : t("table.select_all")
            }
          />
        ) : null}
        <Ellipsis className={tableHeadLabel} title={typeof label === "string" ? label : undefined}>
          {label}
        </Ellipsis>
        {sortable && (
          <ArrowLeftSFill
            size="l"
            className={cx(tableHeadSortIndicator, {
              asc:
                (sort?.column === column ? sort.direction : null) === "asc" ||
                ((sort?.column === column ? sort.direction : null) === null &&
                  defaultSortDirection === "asc"),
              desc:
                (sort?.column === column ? sort.direction : null) === "desc" ||
                ((sort?.column === column ? sort.direction : null) === null &&
                  defaultSortDirection === "desc"),
              active: sorted,
            })}
          />
        )}
      </>
    </th>
  );
};

export default React.memo(Th) as typeof Th;

function nextDirection(current: SortOrder | null, defaultD: SortOrder) {
  if (current === "asc") return "desc";
  if (current === "desc") return "asc";
  return defaultD;
}

const tableHead = css`
  align-items: center;
  background-color: ${colors.titanium100};
  display: flex;
  flex: 1 1 0;
  padding: ${spacings.xs};
  position: relative;
  border-bottom: 1px solid ${colors.titanium200};

  &.sortable {
    cursor: pointer;
    :hover {
      background-color: ${color.titanium200};
    }
  }

  &.sorted {
    background-color: ${color.titanium200};

    &::before {
      background-color: inherit !important;
    }
  }

  ${Ellipsis} {
    margin-right: auto;
  }

  &.left {
    &.sortable ${Ellipsis} {
      padding-right: ${spacings.xs};
    }
  }

  &.center {
    &.sortable ${Ellipsis} {
      padding-left: ${spacings.xs};
      padding-right: ${spacings.xs};
    }
  }

  &.right {
    &.sortable ${Ellipsis} {
      padding-right: ${spacings.xs};
    }
  }

  &.sticky {
    position: sticky;
    z-index: ${zIndex.sticky};

    &.${STICKY_BORDER_RIGHT_CLASSNAME}::before {
      content: "";
      position: absolute;
      width: 3px;
      left: 0;
      top: 0;
      bottom: 0;
      background-color: ${color.titanium100};
      opacity: 0;
      will-change: opacity;
      content-visibility: auto;
    }

    .${SCROLL_RIGHT_CLASSNAME} &.${STICKY_BORDER_RIGHT_CLASSNAME}::before {
      opacity: 1;
    }

    &.${STICKY_BORDER_LEFT_CLASSNAME}::before {
      content: "";
      position: absolute;
      width: 3px;
      right: 0;
      top: 0;
      bottom: 0;
      background-color: ${color.titanium100};
      opacity: 0;
      will-change: opacity;
      content-visibility: auto;
    }

    .${SCROLL_LEFT_CLASSNAME} &.${STICKY_BORDER_LEFT_CLASSNAME}::before {
      opacity: 1;
    }

    &.sorted {
      background-color: ${color.titanium200};
    }
  }

  &.${VERTICAL_SEPARATOR_CLASSNAME}::before {
    content: "";
    position: absolute;
    width: 1px;
    left: 0;
    top: 0;
    bottom: 0;
    background-color: ${color.titanium200};
  }

  &.${VERTICAL_SEPARATOR_CLASSNAME}::after {
    content: attr(data-vertical-separator-label);
    writing-mode: vertical-rl;
    position: absolute;
    color: ${color.titanium900};
    opacity: 0.4;
    left: 2px;
    bottom: 18px;
    white-space: nowrap;
    font-weight: 600;
  }

  .${SELECTABLE_ROWS_CLASSNAME} &:first-of-type {
    padding-left: calc(
      ${ROW_SELECTOR_PADDING_PX}px + ${ROW_SELECTOR_PADDING_PX}px + ${ROW_SELECTOR_SIZE_PX}px
    );
  }

  &.diagonal {
    height: 100px;
    position: relative;
    align-items: flex-end;
  }
`;

const tableHeadLabel = css`
  font: ${fonts.body2SemiBold};
  color: ${colors.carbon800};

  .diagonal & {
    left: 40%;
    position: absolute;
    transform-origin: left bottom;
    transform: translateY(${spacings.xs}) rotate(-20deg);
    width: 240px;
    z-index: ${zIndex.overPositionedElements};
  }
`;

const tableHeadSortIndicator = css`
  --dir: 0deg;
  flex-shrink: 0;
  color: ${colors.carbon800};
  transition: transform ${easingSoftMs}ms ${easingSoft};
  transform: rotateZ(var(--dir));

  &.asc {
    --dir: -90deg;
  }

  &.desc {
    --dir: 90deg;
  }

  &:not(.active) {
    opacity: 0.5;
  }
`;

// const Arrow = styled.div`
//   position: absolute;
//   right: 10px;
//   opacity: 0.5;
//   width: 0;
//   height: 0;
//   border-left: 5px solid transparent;
//   border-right: 5px solid transparent;

//   /* asc */
//   border-top: 5px solid ${color.titanium900};

//   /* desc */
//   transition: transform ${easingSoftMs}ms ${easingSoft};
//   &.desc {
//     transform: rotateX(180deg);
//   }

//   &.active {
//     opacity: 1;
//   }
// `;

// const Content = styled.div`
//   display: flex;
//   align-items: center;
//   width: 100%;
//   font: ${fonts.body2SemiBold};
//   color: ${color.carbon800};

//   > * {
//     flex-shrink: 0;
//   }

//   .sortable & {
//     cursor: pointer;
//   }

//   .diagonal & {
//     position: absolute;
//     z-index: ${zIndex.overPositionedElements};
//     left: 40%;
//     width: 240px;
//     background-color: transparent;
//     padding: 0;
//     transform: rotate(-20deg);
//     transform-origin: left;
//   }
// `;

// const StyledEllipsis = styled(Ellipsis)`
//   width: 100%;
// `;
